import {
  buildSrc,
  srcSetFromWidths,
  sizesForBreakpoints,
  SRCSET_WIDTHS,
  mapTags,
  convertToAnchorLink
} from "$lib/utilities";
import { textToId } from "$lib/utilities/dom-helpers";
import {
  HeroHome,
  HeroPrimary,
  HeroSecondary,
  HeroGeneric,
  HeroArticle,
  HeroPressRelease,
  HeroCtaAboveFold,
  HeroHomepageBackgroundMedia,
  HeroSplit,
  HeroPrimaryBackgroundMedia,
  Markdown
} from "@ObamaFoundation/of-design-system";
import { getImageAspectRatio } from "$lib/utilities/components/image-helpers";
import { isExternalUrl } from "$lib/utilities/url-helpers";
import {
  getVideoComponentPropsForVideoContentType,
  getContentfulVideoComponentProps
} from "$lib/utilities/contentTypes/video";
import { BREAK_POINTS, CTA_TYPE_MAP } from "$lib/utilities/constants";

export const HERO_VARIANTS = {
  CTA_ABOVE_FOLD: "CTA Above Fold",
  DETAIL: "Detail",
  FEATURED_STORY: "Featured Story",
  HOME_LOWER: "Homepage - Lower case",
  HOME_UPPER: "Homepage - Upper case",
  HOMEPAGE_BACKGROUND_MEDIA: "Homepage - Background media",
  HOMEPAGE_SPLIT: "Homepage - Split",
  PRESS_RELEASE: "Press Release",
  PRIMARY_BACKGROUND_MEDIA: "Primary - Background media",
  PRIMARY: "Primary",
  SECONDARY: "Secondary",
  STORY: "Story"
};

export const HERO_COMPONENT_MAP = {
  [HERO_VARIANTS.CTA_ABOVE_FOLD]: HeroCtaAboveFold,
  [HERO_VARIANTS.DETAIL]: HeroGeneric,
  [HERO_VARIANTS.FEATURED_STORY]: HeroArticle,
  [HERO_VARIANTS.STORY]: HeroArticle,
  [HERO_VARIANTS.HOME_LOWER]: HeroHome,
  [HERO_VARIANTS.HOME_UPPER]: HeroHome,
  [HERO_VARIANTS.HOMEPAGE_BACKGROUND_MEDIA]: HeroHomepageBackgroundMedia,
  [HERO_VARIANTS.HOMEPAGE_SPLIT]: HeroSplit,
  [HERO_VARIANTS.PRESS_RELEASE]: HeroPressRelease,
  [HERO_VARIANTS.PRIMARY_BACKGROUND_MEDIA]: HeroPrimaryBackgroundMedia,
  [HERO_VARIANTS.PRIMARY]: HeroPrimary,
  [HERO_VARIANTS.SECONDARY]: HeroSecondary
};

export const HERO_VARIANTS_WITH_THEME = [
  HERO_VARIANTS.HOME_UPPER,
  HERO_VARIANTS.HOME_LOWER,
  HERO_VARIANTS.FEATURED_STORY,
  HERO_VARIANTS.SECONDARY,
  HERO_VARIANTS.CTA_ABOVE_FOLD,
  HERO_VARIANTS.HOMEPAGE_BACKGROUND_MEDIA,
  HERO_VARIANTS.HOMEPAGE_SPLIT,
  HERO_VARIANTS.STORY
];

const HERO_IMAGE_SIZES = {
  [HERO_VARIANTS.DETAIL]: { sm: "100vw", md: "50vw" },
  [HERO_VARIANTS.FEATURED_STORY]: { sm: "100vw", md: "50vw" },
  [HERO_VARIANTS.STORY]: { sm: "100vw", md: "50vw" }
};

/**
 *
 * @param {*} fields
 * @param {string} url the current page's url
 * @description Builds the call to action object for the hero component.
 * @returns
 */
export const buildHeroComponentCta = (fields, url) => {
  if (fields?.callToAction) {
    if (fields.callToAction.fields?.htmlSnippet) {
      // CTA is a html snippet
      return {
        htmlSnippet: fields.callToAction.fields?.htmlSnippet
      };
    } else {
      return {
        text: fields.callToAction.fields?.text,
        url: convertToAnchorLink(fields.callToAction.fields?.destination?.fields?.url, url),
        isExternal: isExternalUrl(fields.callToAction.fields?.destination?.fields?.url),
        id: fields?.title ? `hero-cta-${textToId(fields.title)}` : null
      };
    }
  }
  return null;
};
/**
 *
 * @param {*} fields
 * @param {*} pageData
 * @param {string} url the current page's url
 * @returns {{
 *  formFactor: string,
 *  title: string,
 *  subtitle?: string,
 *  theme?: string,
 *  featuredImage?: Object,
 *  cta?: Object
 * }}
 */
export const getHeroBaseProps = (fields, pageData, url) => {
  const widthsAt = HERO_IMAGE_SIZES[fields.formFactor];
  const sizes = sizesForBreakpoints(widthsAt);
  const showTheme = HERO_VARIANTS_WITH_THEME.includes(fields?.formFactor);

  return {
    formFactor: fields?.formFactor,
    title: fields?.title,
    subtitle: fields?.subheadline,
    theme: showTheme ? pageData?.fields?.theme || fields?.theme : undefined,
    featuredImage: fields?.featuredImage
      ? {
          src: buildSrc(fields.featuredImage.fields?.imageAsset, { w: 3000 }),
          alt: fields.featuredImage.fields?.altText,
          srcset: srcSetFromWidths(
            fields.featuredImage.fields?.imageAsset?.fields?.file?.url,
            SRCSET_WIDTHS
          ),
          sizes,
          eagerLoad: true,
          theme: fields.featuredImage?.fields?.theme // TODO: might need to override in cases where the hero itself is themed
        }
      : null,
    cta: buildHeroComponentCta(fields, url),
    contentfulVideo: fields?.contentfulVideo
      ? getContentfulVideoComponentProps(fields.contentfulVideo, fields.featuredImage)
      : null,
    animatedTitle: fields?.preHeadline,
    displayMediaOverlay: !!fields?.displayMediaOverlay
  };
};

/**
 *
 * @param {*} fields
 * @param {*} pageData
 * @param {string} url the current page's url
 * @returns
 */
export const getHeroVariantProps = (fields, pageData, url) => {
  const actionBar = fields.actionBar
    ? {
        text: fields.actionBar.fields?.text,
        location: fields.actionBar.fields?.venue?.fields,
        showActionItemDataOnMobile: fields.actionBar.fields?.showActionItemDetailsOnMobile,
        actions: fields.actionBar.fields?.actionItems?.map((action) => ({
          descriptionComponent: Markdown,
          title: action.fields?.title,
          description: action.fields?.description,
          cta: action.fields?.cta
            ? {
                url: action.fields?.cta?.fields?.destination?.fields?.url,
                type: action.fields?.cta?.fields?.type
                  ? CTA_TYPE_MAP[action.fields?.cta?.fields?.type]
                  : "textLink",
                text: action.fields?.cta?.fields?.text,
                id: action.fields?.title
                  ? `hero-actionbar-cta-${textToId(action.fields?.title)}`
                  : null
              }
            : null
        }))
      }
    : null;

  const formFactorProps = {
    [HERO_VARIANTS.HOME_UPPER]: {
      ...getHeroBaseProps(fields, pageData, url),
      hasVariableSizeText: true
    },
    [HERO_VARIANTS.HOME_LOWER]: {
      ...getHeroBaseProps(fields, pageData, url)
    },
    [HERO_VARIANTS.PRIMARY]: {
      ...getHeroBaseProps(fields, pageData, url)
    },
    [HERO_VARIANTS.SECONDARY]: {
      ...getHeroBaseProps(fields, pageData, url),
      actionBar,
      featuredStory: fields.featuredStory
        ? {
            title: fields.featuredStory.fields?.text,
            subtitle: fields.featuredStory.fields?.description,
            cta: {
              text: "Learn more",
              url: convertToAnchorLink(fields.featuredStory.fields?.url),
              id: `hero-cta-${textToId(fields.featuredStory.fields?.text)}`
            },
            image: {
              src: buildSrc(fields.featuredStory.fields?.image, { w: 1500 }),
              alt: fields.featuredStory.fields?.image?.fields?.description,
              srcset: srcSetFromWidths(
                fields.featuredStory.fields?.image?.fields?.file?.url,
                SRCSET_WIDTHS
              ),
              sizes: sizesForBreakpoints({ sm: "100vw", md: "50vw" })
            }
          }
        : null,
      featuredVideo: fields.featuredVideo
        ? getVideoComponentPropsForVideoContentType(fields.featuredVideo)
        : null
    },
    [HERO_VARIANTS.DETAIL]: {
      ...getHeroBaseProps(fields, pageData, url),
      secondaryText: fields.secondaryText,
      actionBar
    },
    [HERO_VARIANTS.FEATURED_STORY]: {
      ...getHeroBaseProps(fields, pageData, url),
      imageVariant:
        fields.featuredImage &&
        getImageAspectRatio(fields.featuredImage, fields.featuredImage.fields.aspectRatio),
      tags: mapTags(pageData?.fields?.contentTags, 3, true)
    },
    [HERO_VARIANTS.STORY]: {
      ...getHeroBaseProps(fields, pageData, url),
      imageVariant:
        fields.featuredImage &&
        getImageAspectRatio(fields.featuredImage, fields.featuredImage.fields.aspectRatio),
      tags: mapTags(pageData?.fields?.contentTags, 3, true)
    },
    [HERO_VARIANTS.PRESS_RELEASE]: {
      ...getHeroBaseProps(fields, pageData, url),
      tags: mapTags(pageData?.fields?.contentTags, 3, true)
    },
    [HERO_VARIANTS.CTA_ABOVE_FOLD]: {
      ...getHeroBaseProps(fields, pageData, url)
    },
    [HERO_VARIANTS.PRIMARY_BACKGROUND_MEDIA]: {
      ...getHeroBaseProps(fields, pageData, url)
    },
    [HERO_VARIANTS.HOMEPAGE_BACKGROUND_MEDIA]: {
      ...getHeroBaseProps(fields, pageData, url)
    },
    [HERO_VARIANTS.HOMEPAGE_SPLIT]: {
      ...getHeroBaseProps(fields, pageData, url)
    }
  };

  // filter is the props passed into FilterList ContentType
  const filters = fields.filters;

  return { ...formFactorProps[fields.formFactor], filters };
};

/**
 * This function takes in fields and screenWidth, and returns the breakpoint adjusted fields, also removes the unused fields

 * @param {Object} fields
 * @param {number} screenWidth
 * @returns {Object}
 *
 */

export const getBreakpointAdjustedFields = (fields, screenWidth) => {
  let breakpointAdjustedFields;
  if (
    screenWidth < BREAK_POINTS.md &&
    (fields.mobileFeaturedImage || fields.mobileContentfulVideo)
  ) {
    // if the screen width is less than the medium breakpoint, use the mobile image and video and remove unused fields
    let { desktopContentfulVideo, mobileContentfulVideo, mobileFeaturedImage, ...rest } = {
      ...fields,
      featuredImage: fields.mobileFeaturedImage,
      contentfulVideo: fields.mobileContentfulVideo
    };
    breakpointAdjustedFields = rest;
  } else {
    // if the screen width is greater than the medium breakpoint, use the desktop image and video and remove unused fields
    let { desktopContentfulVideo, mobileContentfulVideo, mobileFeaturedImage, ...rest } = {
      ...fields,
      contentfulVideo: fields.desktopContentfulVideo
    };
    breakpointAdjustedFields = rest;
  }

  return breakpointAdjustedFields;
};

export const getHeroGroupEntryVariant = (formFactor) => {
  if (formFactor === HERO_VARIANTS.HOMEPAGE_SPLIT) {
    return "hero-split";
  }

  if (formFactor === HERO_VARIANTS.HOMEPAGE_BACKGROUND_MEDIA) {
    return "hero-homepage-background-media";
  }

  return null;
};
